import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { Button, Decorator, Input, TimeFromNow } from '@myhealth-raccoon/components';
import { validation } from '@myhealth-raccoon/forms';
import { isRequestError, useAsyncRequest } from '@myhealth-raccoon/services';

import { BackButton } from 'src/components/BackButton.tsx';
import { StickyBottomBar } from 'src/components/StickyBottomBar.tsx';
import { CodeRequestType } from 'src/services/auth';
import { verificationCodeService } from 'src/services/verificationCode';

const Checkbox = ({
    title,
    value,
    checked,
    onClick,
    disabled,
}: {
    title: string;
    value?: string;
    checked?: boolean;
    onClick(): unknown;
    disabled?: boolean;
}) => {
    return (
        <button className={classNames('flex gap-x-3')} onClick={onClick} disabled={disabled} type="button">
            {checked ? (
                <i className="border-primary bg-primary mt-0.5 block h-4 w-4 rounded-full border bg-clip-content p-1"></i>
            ) : disabled ? (
                <i className="mt-0.5 block h-4 w-4 rounded-full border border-gray-300 bg-gray-100"></i>
            ) : (
                <i className="mt-0.5 block h-4 w-4 rounded-full border border-gray-300"></i>
            )}
            <div
                className={classNames('flex flex-col text-left', {
                    'text-primary-700': checked,
                    'text-gray-400': disabled,
                })}
            >
                <span
                    className={classNames('text-sm font-medium', {
                        'text-primary-800': checked,
                        'text-gray-500': disabled,
                        'text-gray-800': !checked && !disabled,
                    })}
                >
                    {title}
                </span>
                <span className="text-sm">{value}</span>
            </div>
        </button>
    );
};

const DELAY = 60000;

type Props = {
    onBack?(): void;
    onSubmit(code: string, type: CodeRequestType): void | Promise<void>;
    maskedEmail?: string;
    maskedMobile?: string;
    className?: string;
};

export function VerificationMethodModal({ onSubmit, onBack, maskedEmail, maskedMobile, className }: Props) {
    const [codeSendType, setCodeSendType] = useState<CodeRequestType>(maskedEmail ? 'Email' : 'Mobile');
    const [codeSent, setCodeSent] = useState(false);
    const { invoke: generateCode, isLoading: isGeneratingCode } = useAsyncRequest({
        method: verificationCodeService.GenerateCode,
        service: verificationCodeService,
    });

    const [resendTime, setResendTime] = useState<number>();
    const [resendIsDisabled, setResendIsDisabled] = useState<boolean>();

    const {
        handleSubmit,
        register,
        formState: { isSubmitting, errors },
        reset,
        setError,
    } = useForm<{ code: string }>();
    const codeInput = register('code', {
        validate: validation.composeValidators(
            validation.required('Please enter your six digit code'),
            validation.regex(/^[0-9]{6}$/, 'Please enter your six digit code')
        ),
    });

    const resendCode = useCallback(
        async (type: CodeRequestType) => {
            try {
                await generateCode(type);
                reset();
                toast("We've sent a new code!");
                setResendIsDisabled(true);
            } catch {
                /* */
            }
        },
        [generateCode, reset]
    );

    // Reset form state when code type is changed
    useEffect(() => {
        reset();
        setResendIsDisabled(false);
        setCodeSent(false);
    }, [codeSendType, reset]);

    // SET DELAY WHEN resendIsDisabled === true
    useEffect(() => {
        if (resendIsDisabled) {
            const timeout = setTimeout(() => {
                setResendIsDisabled(false);
                setResendTime(undefined);
            }, DELAY);
            setResendTime(Date.now() + DELAY);

            return () => clearTimeout(timeout);
        }
    }, [resendIsDisabled]);

    const verificationCodeForm = useCallback(
        (type: CodeRequestType) =>
            codeSendType === type && (
                <div className="flex flex-col items-start gap-4 rounded-xl border border-gray-200 bg-gray-50 p-4 pr-8 text-sm text-gray-800">
                    {!codeSent ? (
                        <>
                            <p>
                                We’ll send a code to your {type === 'Mobile' ? 'mobile number' : 'email'}. Please enter
                                the code to verify your identity.
                            </p>
                            <Button
                                disabled={isGeneratingCode}
                                onClick={async () => {
                                    await generateCode(type);
                                    setCodeSent(true);
                                }}
                                variant="outline"
                            >
                                Send code
                            </Button>
                        </>
                    ) : (
                        <>
                            <p>Your 6-digit code is on its way! The code will expire after 5 minutes.</p>
                            <Input
                                id={type + '-verification-code'}
                                className="w-full"
                                label="6-digit code"
                                type="text"
                                inputMode="numeric"
                                autoFocus
                                {...codeInput}
                                requiredIndicator
                                hint={
                                    <>
                                        Didn’t receive the code?{' '}
                                        <button
                                            disabled={isGeneratingCode || resendIsDisabled}
                                            onClick={() => resendCode(type)}
                                            type="button"
                                            className="text-primary font-semibold hover:underline disabled:pointer-events-none disabled:text-gray-500"
                                        >
                                            Click to resend
                                        </button>{' '}
                                        {resendIsDisabled && resendTime && <TimeFromNow explicit time={resendTime} />}
                                    </>
                                }
                            />
                            <Decorator fieldError={errors.code} />
                        </>
                    )}
                </div>
            ),
        [
            codeInput,
            codeSendType,
            codeSent,
            errors.code,
            generateCode,
            isGeneratingCode,
            resendCode,
            resendIsDisabled,
            resendTime,
        ]
    );

    return (
        <form
            className={classNames('flex h-full flex-col pt-5', className)}
            onSubmit={handleSubmit(async ({ code }) => {
                try {
                    await onSubmit(code, codeSendType);
                } catch (e) {
                    if (isRequestError(e)) {
                        setError('code', { message: e.message });
                    }
                }
            })}
        >
            <div className="flex flex-col gap-y-5 px-5 pb-5">
                <Checkbox
                    disabled={!maskedEmail}
                    title="Verification via Email"
                    value={maskedEmail || 'Not provided'}
                    checked={codeSendType === 'Email'}
                    onClick={() => setCodeSendType('Email')}
                />
                {verificationCodeForm('Email')}
                <div className="flex select-none flex-row items-center gap-2 text-gray-400">
                    <hr className="flex-1" />
                    OR
                    <hr className="flex-1" />
                </div>
                <Checkbox
                    title="Verification via mobile"
                    value={maskedMobile}
                    checked={codeSendType === 'Mobile'}
                    onClick={() => setCodeSendType('Mobile')}
                />
                {verificationCodeForm('Mobile')}
            </div>
            <StickyBottomBar className={'mt-auto justify-end gap-3 p-5'}>
                {onBack && <BackButton label={'Back'} onClick={onBack} />}
                <Button color="primary" type="submit" disabled={isSubmitting || !codeSent}>
                    Verify
                </Button>
            </StickyBottomBar>
        </form>
    );
}
